import GlobalSearch from "./GlobalSearch";
import ListLoading from "./ListLoading";
import { notificationText } from "../lib/notificationLabels";
import { startVisiblePolling } from "../lib/visiblePolling";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBillingAccess } from "../context/BillingAccessContext";
import {
  clearVisitAuthSession,
  getVisitAuthReturnUrl,
  messengerAPI,
  notificationAPI,
  patientAcquisitionAPI,
  setupAPI,
  shopAPI,
  stripeAPI,
  subscriptionAPI,
  staffAPI,
} from "../lib/api";
import { useActiveLocation } from "../context/ActiveLocationContext";
import { useTerminology } from "../context/TerminologyContext";
import {
  LayoutDashboard, Calendar, ClipboardList, Users, UserCircle, BarChart3, Settings,
  LogOut, Menu, X, ChevronRight, ChevronDown, Building2, CreditCard, ShoppingBag,
  Bell, CheckCircle2, CheckCheck, XCircle, MessageSquare, Sparkles, Gift, Image, Megaphone, UserPlus
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import SetupTourOverlay from "./SetupTourOverlay";
import { buildSetupTourTarget, dispatchSetupTourStatusChanged, readSetupTourState, SETUP_PAYMENTS_STATUS_EVENT, SETUP_TOUR_EVENT } from "../lib/setupTour";
import PaymentSetupIndicator from "./PaymentSetupIndicator";
import { needsPaymentSetup, onlinePaymentsAvailable, PAYMENT_SETUP_URL } from "../lib/paymentSetup";
import { alphaColor, getSidebarTheme, resolveDesignTheme } from "../lib/designThemes";
import {
  buildNavigation,
  EXTRA_ALLOWED_PATHS,
  HIDDEN_PERMISSION_ROUTES,
  NAV_TOUR_IDS,
} from "../lib/navigation";
import { getSubscriptionDaysRemaining } from "../lib/subscriptionDisplay";
import ReleaseNotesDialog from "./ReleaseNotesDialog";
import { FALLBACK_RELEASE_NOTES, fetchReleaseNotes } from "../lib/releaseNotes";

const isAbortLike = (err) =>
  err.name === "AbortError" ||
  err.name === "CanceledError" ||
  err.code === "ERR_CANCELED" ||
  String(err.message || "").toLowerCase().includes("aborted");

export function isSidebarFeatureEnabled(key, featureFlags, { loaded, loadFailed } = {}) {
  if (!key) return true;
  if (!loaded || loadFailed) return true;
  if (!featureFlags || typeof featureFlags !== "object") return false;
  if (key === "vouchers" || key === "gift_cards") {
    return featureFlags.vouchers === true || featureFlags.gift_cards === true;
  }
  return featureFlags[key] === true;
}

function formatNotificationRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat("de", { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(diffDays, "day");
}

function formatNotificationDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getNotificationIcon(eventType) {
  if (eventType === "appointment_cancelled") return XCircle;
  if (eventType === "appointment_paid") return CreditCard;
  if (eventType === "lead_created") return UserPlus;
  return CheckCircle2;
}

function getNotificationIconClass(eventType) {
  if (eventType === "appointment_cancelled") return "text-[#FF3B30]";
  if (eventType === "appointment_paid") return "text-[#34C759]";
  if (eventType === "lead_created") return "text-[#0284C7]";
  return "text-[#0071E3]";
}

export function findNewestUnreadLead(items, previousIds) {
  const known = previousIds instanceof Set ? previousIds : new Set(previousIds || []);
  return (items || []).find((item) => (
    item?.event_type === "lead_created" && item?.id && !known.has(item.id)
  )) || null;
}

export function isNotificationUnread(item, readAt, readIds = new Set()) {
  if (!item?.happened_at) return false;
  if (item?.id && readIds?.has?.(item.id)) return false;
  const happenedAt = new Date(item.happened_at).getTime();
  const readAtTime = readAt ? new Date(readAt).getTime() : Number.NEGATIVE_INFINITY;
  if (Number.isNaN(happenedAt)) return false;
  return happenedAt > (Number.isNaN(readAtTime) ? Number.NEGATIVE_INFINITY : readAtTime);
}

export function formatNotificationBadgeCount(count) {
  const normalizedCount = Math.max(0, Number(count) || 0);
  return normalizedCount > 25 ? "25+" : String(normalizedCount);
}

export function getNotificationReadMarker(items, now = Date.now()) {
  const latestVisibleTime = Math.max(
    now,
    ...(items || [])
      .map((item) => new Date(item?.happened_at || 0).getTime())
      .filter(Number.isFinite),
  );
  return new Date(latestVisibleTime).toISOString();
}

function blockUnsavedClientNavigation(path = null) {
  if (typeof window === "undefined" || window.__bookingUnsavedNavigationDirty !== true) return false;
  window.dispatchEvent(new CustomEvent("booking:unsaved-client-navigation", { detail: { path } }));
  return true;
}

// Keep navigation and its live counters mounted while a route chunk loads.
export function DashboardPageContent({ children }) {
  return (
    <Suspense fallback={
      <div role="status" className="flex min-h-64 items-center justify-center gap-3 text-sm text-[#86868B]">
        <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        Bereich wird geladen …
      </div>
    }>
      {children}
    </Suspense>
  );
}

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { org, vocab } = useTerminology();
  const [featureFlags, setFeatureFlags] = useState({});
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [subscriptionLoadFailed, setSubscriptionLoadFailed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [stripeConnectStatus, setStripeConnectStatus] = useState({ connected: false, status: "not_connected" });
  const [shopOrdersBadge, setShopOrdersBadge] = useState(0);
  const [paymentSetupStatus, setPaymentSetupStatus] = useState(null);
  const paymentStatusVersion = useRef(0);
  const [messengerUnreadBadge, setMessengerUnreadBadge] = useState(0);
  const [newInquiryBadge, setNewInquiryBadge] = useState(0);
  const [messengerAlert, setMessengerAlert] = useState(null);
  const [leadAlert, setLeadAlert] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationItems, setNotificationItems] = useState([]);
  const [notificationUnread, setNotificationUnread] = useState(0);
  const [notificationSessionNewIds, setNotificationSessionNewIds] = useState(() => new Set());
  const [layoutReady, setLayoutReady] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupTourState, setSetupTourState] = useState(() => readSetupTourState());
  const [roleResolved, setRoleResolved] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState(null);
  const [releaseNotesLoading, setReleaseNotesLoading] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [messengerHeaderVisible, setMessengerHeaderVisible] = useState(true);
  const [searchActive, setSearchActive] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const guardedNavigate = useCallback((path) => {
    if (blockUnsavedClientNavigation(path)) return false;
    navigate(path);
    return true;
  }, [navigate]);
  const isAutoHideHeaderPage = location.pathname === "/messenger" || location.pathname === "/calendar";
  const { user, logout } = useAuth();
  const billingAccess = useBillingAccess();
  const softwareAccessRestricted = billingAccess.scope === "software" && billingAccess.mode === "restricted";
  const smsAccessRestricted = billingAccess.mode === "restricted" && !softwareAccessRestricted;
  const smsPaymentWarning = ["sms_invoice_unpaid", "sms_payment_processing"].includes(billingAccess.reason)
    && ["grace", "restricted", "processing"].includes(billingAccess.mode);
  const bootstrapRef = useRef(false);
  const messengerSummaryRef = useRef({ loaded: false, unreadCount: 0, latestInboundAt: null });
  const notificationFeedRef = useRef({ loaded: false, ids: new Set() });
  const notificationScrollRef = useRef(null);
  const messengerAudioContextRef = useRef(null);
  const messengerAudioUnlockedRef = useRef(false);
  const messengerAlertTimeoutRef = useRef(null);
  const leadAlertTimeoutRef = useRef(null);
  const playAlertSoundRef = useRef(() => {});
  const messengerHeaderVisibleRef = useRef(true);
  const messengerHeaderPointerYRef = useRef(Number.POSITIVE_INFINITY);
  const messengerHeaderFrameRef = useRef(null);
  const { locations, activeLocationId, setActiveLocationId, activeLocation, canSelectAll } = useActiveLocation();

  useEffect(() => {
    if (!isAutoHideHeaderPage || typeof window === "undefined" || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      messengerHeaderVisibleRef.current = true;
      setMessengerHeaderVisible(true);
      return undefined;
    }
    const revealZoneBottom = 92;
    const hideZoneTop = 128;
    messengerHeaderVisibleRef.current = false;
    messengerHeaderPointerYRef.current = Number.POSITIVE_INFINITY;
    setMessengerHeaderVisible(false);

    const updateHeaderVisibility = () => {
      messengerHeaderFrameRef.current = null;
      const pointerY = messengerHeaderPointerYRef.current;
      const nextVisible = messengerHeaderVisibleRef.current
        ? pointerY < hideZoneTop
        : pointerY <= revealZoneBottom;
      if (nextVisible === messengerHeaderVisibleRef.current) return;
      messengerHeaderVisibleRef.current = nextVisible;
      setMessengerHeaderVisible(nextVisible);
    };

    const handlePointerMove = (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      messengerHeaderPointerYRef.current = Number(event.clientY || 0);
      if (messengerHeaderFrameRef.current !== null) return;
      messengerHeaderFrameRef.current = window.requestAnimationFrame(updateHeaderVisibility);
    };
    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      if (messengerHeaderFrameRef.current !== null) {
        window.cancelAnimationFrame(messengerHeaderFrameRef.current);
        messengerHeaderFrameRef.current = null;
      }
    };
  }, [isAutoHideHeaderPage]);
  const authUser = user || {};
  const roleRaw = (authUser.role || "").toLowerCase();
  const role = ["owner", "admin", "reception", "staff"].includes(roleRaw) ? roleRaw : "staff";
  const hasFeature = (key) => {
    // Ein voruebergehender Fehler des Plan-Endpunkts darf nicht Marketing,
    // Berichte und weitere gebuchte Bereiche aus der Navigation entfernen.
    // Der Server prueft die Berechtigung beim Aufruf weiterhin verbindlich.
    return isSidebarFeatureEnabled(key, featureFlags, {
      loaded: subscriptionLoaded,
      loadFailed: subscriptionLoadFailed,
    });
  };
  const hasPermission = (key) => Array.isArray(authUser.permissions) && authUser.permissions.includes(key);
  const canAccessMessenger = hasPermission("messenger_access");
  const canAccessInquiries = hasPermission("patient_acquisition:read") || hasPermission("reports_access") || ["owner", "admin"].includes(role);
  const canAccessNewsletter = hasPermission("newsletter_access");
  /* Aufgeklappt ist, wo man gerade ist. Ein manuelles Auf- oder Zuklappen gilt nur bis
     zum nächsten Seitenwechsel – sonst bliebe Marketing dauerhaft offen stehen, während
     Kalender und Kunden sich beim Wegklicken schließen. */
  const [manualGroups, setManualGroups] = useState({});
  const toggleGroup = (name, expanded) => setManualGroups((prev) => ({ ...prev, [name]: !expanded }));
  useEffect(() => { setManualGroups({}); }, [location.pathname]);
  const [matchingStaffAvatar, setMatchingStaffAvatar] = useState(null);

  const loadReleaseNotes = useCallback(async () => {
    setReleaseNotesLoading(true);
    try {
      const notes = await fetchReleaseNotes();
      setReleaseNotes(notes);
      return notes;
    } catch {
      setReleaseNotes(FALLBACK_RELEASE_NOTES);
      return FALLBACK_RELEASE_NOTES;
    } finally {
      setReleaseNotesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReleaseNotes();
  }, [loadReleaseNotes]);

  const openVersionHistory = useCallback(() => {
    setVersionHistoryOpen(true);
    void loadReleaseNotes();
  }, [loadReleaseNotes]);

  useEffect(() => {
    setMatchingStaffAvatar(null);
    if (!authUser?.email || smsAccessRestricted) return;
    let active = true;
    staffAPI.list()
      .then((res) => {
        if (!active) return;
        const staffList = Array.isArray(res?.data) ? res.data : [];
        const match = staffList.find(s => String(s.email).toLowerCase() === String(authUser.email).toLowerCase());
        if (match?.avatar_url) {
          setMatchingStaffAvatar(match.avatar_url);
        }
      })
      .catch((e) => console.error("Failed to load staff list for avatar matching", e));
    return () => {
      active = false;
    };
  }, [authUser?.email, smsAccessRestricted]);
  const handleShopSummaryError = useCallback((error) => {
    const status = error?.response?.status;
    if (status && status !== 403) {
      console.error("Shop summary load failed:", error);
    }
    setShopOrdersBadge(0);
  }, []);
  const notificationsStorageKey = `dashboard_notifications_read_at:${authUser?.org_id || org?.id || "org"}:${authUser?.id || authUser?.email || "user"}`;
  const notificationReadIdsStorageKey = `${notificationsStorageKey}:ids`;

  const getNotificationsReadAt = useCallback(() => {
    try {
      return localStorage.getItem(notificationsStorageKey) || "";
    } catch {
      return "";
    }
  }, [notificationsStorageKey]);

  const writeNotificationsReadAt = useCallback((value) => {
    try {
      localStorage.setItem(notificationsStorageKey, value);
    } catch {
      // ignore
    }
  }, [notificationsStorageKey]);

  const getNotificationReadIds = useCallback(() => {
    try {
      const value = JSON.parse(localStorage.getItem(notificationReadIdsStorageKey) || "[]");
      return new Set(Array.isArray(value) ? value.filter(Boolean) : []);
    } catch {
      return new Set();
    }
  }, [notificationReadIdsStorageKey]);

  const writeNotificationReadIds = useCallback((ids) => {
    try {
      localStorage.setItem(notificationReadIdsStorageKey, JSON.stringify(Array.from(ids).slice(-100)));
    } catch {
      // ignore
    }
  }, [notificationReadIdsStorageKey]);

  const syncNotificationUnread = useCallback((items) => {
    const readAt = getNotificationsReadAt();
    const readIds = getNotificationReadIds();
    setNotificationUnread((items || []).filter((item) => isNotificationUnread(item, readAt, readIds)).length);
  }, [getNotificationReadIds, getNotificationsReadAt]);

  const markAllNotificationsRead = useCallback(() => {
    writeNotificationsReadAt(getNotificationReadMarker(notificationItems));
    writeNotificationReadIds(new Set());
    setNotificationUnread(0);
    setNotificationSessionNewIds(new Set());
  }, [notificationItems, writeNotificationReadIds, writeNotificationsReadAt]);

  const markNotificationRead = useCallback((item) => {
    if (!item?.id) return;
    const readAt = getNotificationsReadAt();
    const readIds = getNotificationReadIds();
    if (!isNotificationUnread(item, readAt, readIds)) return;
    readIds.add(item.id);
    writeNotificationReadIds(readIds);
    setNotificationUnread((count) => Math.max(0, count - 1));
    setNotificationSessionNewIds((ids) => {
      const nextIds = new Set(ids);
      nextIds.delete(item.id);
      return nextIds;
    });
  }, [getNotificationReadIds, getNotificationsReadAt, writeNotificationReadIds]);

  const openNotificationTarget = useCallback((item) => {
    markNotificationRead(item);
    setNotificationsOpen(false);
    setNotificationSessionNewIds(new Set());
    const eventType = String(item?.event_type || "").toLowerCase();
    const appointmentId = String(item?.appointment_id || "").trim();
    const threadId = String(item?.thread_id || item?.message_thread_id || "").trim();
    const targetUrl = String(item?.target_url || "").trim();
    if (threadId || eventType.includes("messenger") || eventType.includes("message")) {
      const query = threadId ? `?thread_id=${encodeURIComponent(threadId)}` : "";
      guardedNavigate(`/messenger${query}`);
      return;
    }
    if (appointmentId) {
      guardedNavigate(`/calendar?appointment_id=${encodeURIComponent(appointmentId)}&open=1`);
      return;
    }
    if (targetUrl.startsWith("/")) {
      guardedNavigate(targetUrl);
      return;
    }
    guardedNavigate("/calendar");
  }, [guardedNavigate, markNotificationRead]);

  const loadNotifications = useCallback(async ({ notify = false, signal } = {}) => {
    setNotificationsLoading(true);
    try {
      const res = await notificationAPI.getFeed({ limit: 100 }, { signal });
      if (signal?.aborted) return;
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      const previous = notificationFeedRef.current || { loaded: false, ids: new Set() };
      if (notify && previous.loaded) {
        const newLead = findNewestUnreadLead(items, previous.ids);
        if (newLead) {
          playAlertSoundRef.current();
          setLeadAlert({
            title: newLead.client_name || `Neuer ${vocab.person.sg}`,
            body: notificationText([newLead.service_name, newLead.campaign || newLead.source].filter(Boolean).join(" · ")) || "Neue Anfrage im Bereich Anfragen",
            targetUrl: newLead.target_url || "/dashboard",
          });
          if (leadAlertTimeoutRef.current) window.clearTimeout(leadAlertTimeoutRef.current);
          leadAlertTimeoutRef.current = window.setTimeout(() => setLeadAlert(null), 7000);
          window.dispatchEvent(new CustomEvent("lead-received", { detail: newLead }));
        }
      }
      notificationFeedRef.current = {
        loaded: true,
        ids: new Set(items.map((item) => item?.id).filter(Boolean)),
      };
      setNotificationItems(items.slice(0, 25));
      syncNotificationUnread(items);
    } catch (error) {
      if (signal?.aborted || isAbortLike(error)) return;
      console.warn("Notification feed load failed:", error);
      setNotificationItems([]);
      setNotificationUnread(0);
    } finally {
      setNotificationsLoading(false);
    }
  }, [syncNotificationUnread, vocab.person.sg]);

  useEffect(() => () => {
    if (messengerAlertTimeoutRef.current) {
      window.clearTimeout(messengerAlertTimeoutRef.current);
    }
    if (leadAlertTimeoutRef.current) {
      window.clearTimeout(leadAlertTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return undefined;

    const unlockAudio = () => {
      try {
        if (!messengerAudioContextRef.current) {
          messengerAudioContextRef.current = new AudioContext();
        }
        const context = messengerAudioContextRef.current;
        if (context.state === "suspended") {
          context.resume().catch(() => {});
        }
        messengerAudioUnlockedRef.current = context.state !== "closed";
      } catch {
        messengerAudioUnlockedRef.current = false;
      }
    };

    window.addEventListener("pointerdown", unlockAudio, { passive: true });
    window.addEventListener("keydown", unlockAudio);
    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const playMessengerSound = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!messengerAudioUnlockedRef.current) return;
    try {
      const context = messengerAudioContextRef.current;
      if (!context || context.state === "closed") return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(1046, context.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(784, context.currentTime + 0.16);
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.24);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.25);
    } catch {
      // ignore
    }
  }, []);
  playAlertSoundRef.current = playMessengerSound;

  const loadMessengerSummary = useCallback(async ({ notify = false, signal } = {}) => {
    if (!canAccessMessenger || featureFlags?.messenger === false) {
      setMessengerUnreadBadge(0);
      messengerSummaryRef.current = { loaded: true, unreadCount: 0, latestInboundAt: null };
      return;
    }
    try {
      const res = await messengerAPI.getSummary({ signal });
      if (signal?.aborted) return;
      const summary = res?.data || {};
      const unreadCount = Number(summary.unread_count || 0);
      const previous = messengerSummaryRef.current || {};
      setMessengerUnreadBadge(unreadCount);
      if (
        notify &&
        previous.loaded &&
        unreadCount > Number(previous.unreadCount || 0) &&
        summary.latest_inbound_at &&
        summary.latest_inbound_at !== previous.latestInboundAt
      ) {
        playMessengerSound();
        const threadId = String(summary.latest_thread_id || "").trim();
        setMessengerAlert({
          title: `Neue SMS von ${summary.latest_client_name || vocab.person.sg}`,
          body: summary.latest_preview || "Neue Antwort im Messenger",
          threadId,
        });
        if (messengerAlertTimeoutRef.current) {
          window.clearTimeout(messengerAlertTimeoutRef.current);
        }
        messengerAlertTimeoutRef.current = window.setTimeout(() => {
          setMessengerAlert(null);
        }, 5000);
      }
      messengerSummaryRef.current = {
        loaded: true,
        unreadCount,
        latestInboundAt: summary.latest_inbound_at || null,
      };
    } catch (error) {
      if (signal?.aborted || isAbortLike(error)) return;
      const status = error?.response?.status;
      if (status && status !== 403) {
        console.warn("Messenger summary load failed:", error);
      }
      setMessengerUnreadBadge(0);
    }
  }, [canAccessMessenger, featureFlags?.messenger, playMessengerSound, vocab.person.sg]);

  const roleNavigation = buildNavigation(vocab).filter((item) => item.roles.includes(role));
  const visibleNavigation = roleNavigation.map((item) => {
    if (!Array.isArray(item.children)) return item;
    return {
      ...item,
      children: item.children.filter((child) => {
        /* Kinder tragen jetzt auch Rollen – ohne das sähe eine Staff-Rolle Einträge,
           die für sie gesperrt sind. */
        const roleOk = !child.roles || child.roles.includes(role);
        const permOk = !child.permission || hasPermission(child.permission);
        const featureOk = !child.feature || hasFeature(child.feature);
        return roleOk && permOk && featureOk;
      }),
    };
  }).filter((item) => {
    if (item.feature && !hasFeature(item.feature)) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    /* Eine Gruppe ohne verbleibende Kinder verschwindet nur dann, wenn sie kein
       eigenes Ziel hat – sonst bleibt sie als normaler Link stehen. */
    if (Array.isArray(item.children) && item.children.length === 0 && !item.href) return false;
    return true;
  }).map((item) => {
    if (Array.isArray(item.children) && item.children.length === 0) {
      const { children, ...rest } = item;
      return rest;
    }
    return item;
  }).filter((item) => !smsAccessRestricted || item.href === "/calendar").map((item) => {
    if (!smsAccessRestricted || item.href !== "/calendar") return item;
    const { children, ...calendarOnly } = item;
    return calendarOnly;
  });

  useEffect(() => {
    const scope = `${authUser.org_id || ''}:${role}:${smsAccessRestricted}`;
    if (bootstrapRef.current === scope) return;
    bootstrapRef.current = scope;
    setPaymentSetupStatus(null);

    const controller = new AbortController();
    const paymentVersion = paymentStatusVersion.current;
    let subscriptionRetryId = null;
    const applySubscription = (value) => {
      const sub = value || {};
      setFeatureFlags(sub.feature_flags || {});
      setSubscription(sub);
      setSubscriptionLoadFailed(false);
    };
    (async () => {
      try {
        const [subRes, stripeRes] = await Promise.all([
          subscriptionAPI.get({ signal: controller.signal }),
          smsAccessRestricted
            ? Promise.resolve({ data: { connected: false, status: "not_connected" } })
            : stripeAPI.connectStatus({ signal: controller.signal }).catch(() => ({ data: null })),
        ]);
        if (controller.signal.aborted || bootstrapRef.current !== scope) return;
        applySubscription(subRes.data);
        setSubscriptionLoaded(true);
        const connectData = stripeRes?.data || {};
        if (paymentVersion === paymentStatusVersion.current) {
          setPaymentSetupStatus({ orgId: authUser.org_id, status: stripeRes?.data || null });
          setStripeConnectStatus({
            connected: Boolean(connectData.connected),
            status: String(connectData.status || "not_connected"),
          });
        }
        if (!smsAccessRestricted && ["owner", "admin"].includes(role)) {
          try {
            const setupRes = await setupAPI.getStatus({ signal: controller.signal });
            setSetupStatus(setupRes.data || null);
            setSetupTourState(readSetupTourState());
          } catch (setupErr) {
            if (!isAbortLike(setupErr)) {
              console.warn("Failed to load setup status:", setupErr);
            }
          }
        }
      } catch (e) {
        if (isAbortLike(e)) return;
        console.error("Failed to load layout data:", e);
        setSubscriptionLoadFailed(true);
        // Transiente API- oder Netzwerkfehler selbststaendig heilen. Bis dahin
        // bleibt die Navigation sichtbar, statt den Plan faelschlich zu kuerzen.
        subscriptionRetryId = window.setTimeout(() => {
          subscriptionAPI.get()
            .then((response) => applySubscription(response.data))
            .catch((retryError) => {
              if (!isAbortLike(retryError)) console.warn("Failed to reload subscription:", retryError);
            });
        }, 5000);
      } finally {
        setSubscriptionLoaded(true);
        setLayoutReady(true);
      }
    })();
    return () => {
      controller.abort();
      if (bootstrapRef.current === scope) bootstrapRef.current = null;
      if (subscriptionRetryId !== null) window.clearTimeout(subscriptionRetryId);
    };
  }, [role, smsAccessRestricted, authUser.org_id]);

  useEffect(() => {
    const onPaymentsStatus = event => {
      if (event.detail?.orgId !== authUser.org_id) return;
      const status = event.detail.status;
      if (!status) return;
      paymentStatusVersion.current += 1;
      setPaymentSetupStatus({ orgId: authUser.org_id, status });
      setStripeConnectStatus({ connected: Boolean(status.connected), status: status.status || "not_connected" });
    };
    window.addEventListener(SETUP_PAYMENTS_STATUS_EVENT, onPaymentsStatus);
    return () => window.removeEventListener(SETUP_PAYMENTS_STATUS_EVENT, onPaymentsStatus);
  }, [authUser.org_id]);

  useEffect(() => {
    const refreshSetupStatus = () => {
      if (!["owner", "admin"].includes(role)) return;
      setupAPI.getStatus()
        .then((res) => {
          setSetupStatus(res.data || null);
          setSetupTourState(readSetupTourState());
        })
        .catch(() => {});
    };
    window.addEventListener(SETUP_TOUR_EVENT, refreshSetupStatus);
    return () => window.removeEventListener(SETUP_TOUR_EVENT, refreshSetupStatus);
  }, [role]);

  useEffect(() => {
    if (!roleResolved && (authUser.role || authUser.email || authUser.id)) {
      setRoleResolved(true);
    }
  }, [authUser.role, authUser.email, authUser.id, roleResolved]);

  useEffect(() => {
    let cancelled = false;
    if (!subscriptionLoaded || smsAccessRestricted) {
      return () => {
        cancelled = true;
      };
    }
    const shopEnabled = featureFlags?.shop !== false;
    if (!shopEnabled) {
      setShopOrdersBadge(0);
      return () => {
        cancelled = true;
      };
    }
    shopAPI.summary(activeLocationId ? { location_id: activeLocationId } : undefined)
      .then((res) => {
        if (cancelled) return;
        const unread = Number(res?.data?.unread_count ?? res?.data?.pending_orders ?? 0);
        setShopOrdersBadge(unread);
      })
      .catch((error) => {
        if (cancelled) return;
        handleShopSummaryError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [activeLocationId, featureFlags, handleShopSummaryError, subscriptionLoaded, smsAccessRestricted]);

  const refreshShopSummary = useCallback(() => {
    if (!subscriptionLoaded || smsAccessRestricted) return;
    if (featureFlags?.shop === false) return;
    shopAPI.summary(activeLocationId ? { location_id: activeLocationId } : undefined)
      .then((res) => setShopOrdersBadge(Number(res?.data?.unread_count ?? res?.data?.pending_orders ?? 0)))
      .catch(handleShopSummaryError);
  }, [activeLocationId, featureFlags?.shop, handleShopSummaryError, subscriptionLoaded, smsAccessRestricted]);

  useEffect(() => {
    const handler = () => refreshShopSummary();
    window.addEventListener("shop-orders-viewed", handler);
    return () => window.removeEventListener("shop-orders-viewed", handler);
  }, [refreshShopSummary]);

  useEffect(() => {
    if (!layoutReady || smsAccessRestricted) return;
    return startVisiblePolling((signal) => loadNotifications({ notify: true, signal }));
  }, [activeLocationId, layoutReady, loadNotifications, smsAccessRestricted]);

  const isMessengerPage = location.pathname === "/messenger";
  useEffect(() => {
    if (!layoutReady || !canAccessMessenger || smsAccessRestricted) return;
    return startVisiblePolling((signal) => loadMessengerSummary({ notify: true, signal }), {
      events: ["messenger-unread-updated"],
    });
  }, [canAccessMessenger, isMessengerPage, layoutReady, loadMessengerSummary, smsAccessRestricted]);

  useEffect(() => {
    if (!layoutReady || !canAccessInquiries || smsAccessRestricted || featureFlags?.patient_acquisition === false) {
      setNewInquiryBadge(0);
      return undefined;
    }
    return startVisiblePolling(async (signal) => {
      try {
        const response = await patientAcquisitionAPI.newInquiryCount(undefined, { signal });
        if (!signal.aborted) setNewInquiryBadge(Number(response?.data?.total || 0));
      } catch (error) {
        if (!signal.aborted && error?.response?.status !== 403) console.warn("New inquiry count failed:", error);
      }
    }, { events: ["lead-received", "patient-acquisition-updated"] });
  }, [canAccessInquiries, featureFlags?.patient_acquisition, layoutReady, smsAccessRestricted]);

  useEffect(() => {
    if (!notificationsOpen || notificationItems.length === 0) return;
    const readAt = getNotificationsReadAt();
    const readIds = getNotificationReadIds();
    const unreadIds = notificationItems
      .filter((item) => item?.id && isNotificationUnread(item, readAt, readIds))
      .map((item) => item.id);
    setNotificationSessionNewIds(new Set(unreadIds));
  }, [getNotificationReadIds, getNotificationsReadAt, notificationItems, notificationsOpen]);

  useEffect(() => {
    if (!notificationsOpen || notificationsLoading || !notificationScrollRef.current) return undefined;
    const scrollRoot = notificationScrollRef.current;
    const markVisible = (element) => {
      const item = notificationItems.find(entry => String(entry.id) === element.dataset.notificationId);
      if (item) markNotificationRead(item);
    };
    const unreadElements = [...scrollRoot.querySelectorAll('[data-notification-state="new"]')];
    if (typeof IntersectionObserver === 'undefined') {
      const markInViewport = () => unreadElements.filter(element => {
        const rect = element.getBoundingClientRect();
        const rootRect = scrollRoot.getBoundingClientRect();
        return rect.top >= rootRect.top && rect.bottom <= rootRect.bottom;
      }).forEach(markVisible);
      markInViewport();
      scrollRoot.addEventListener('scroll', markInViewport);
      return () => scrollRoot.removeEventListener('scroll', markInViewport);
    }
    const observer = new IntersectionObserver(entries => {
      entries.filter(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)
        .forEach(entry => { markVisible(entry.target); observer.unobserve(entry.target); });
    }, { root: scrollRoot, threshold: 0.5 });
    unreadElements.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [markNotificationRead, notificationItems, notificationSessionNewIds, notificationsLoading, notificationsOpen]);

  useEffect(() => {
    if (!layoutReady || !roleResolved) return;

    if (smsAccessRestricted) {
      if (!["/calendar", "/upgrade"].includes(location.pathname)) navigate("/calendar", { replace: true });
      return;
    }

    /* Gruppen haben inzwischen selbst ein Ziel (Kalender, Kunden) – deren href muss
       zusätzlich zu den Kindern in die Liste, sonst sperrt der Guard sie aus. */
    const allowed = visibleNavigation.flatMap((item) => [
      ...(item.href ? [item.href] : []),
      ...(Array.isArray(item.children) ? item.children.map((c) => c.href).filter(Boolean) : []),
    ]);
    if (role === "owner" || role === "admin") allowed.push("/api-docs");
    allowed.push(...EXTRA_ALLOWED_PATHS);
    allowed.push(...HIDDEN_PERMISSION_ROUTES.filter(route => route.roles.includes(role) && authUser.permissions?.includes(route.permission)).map(route => route.href));
    const isAllowed = allowed.some((path) => location.pathname.startsWith(path));
    if (!isAllowed) {
      navigate("/dashboard", { replace: true });
    }
  }, [layoutReady, roleResolved, location.pathname, navigate, role, visibleNavigation, authUser.permissions, smsAccessRestricted]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const handleOpenPayments = useCallback(async () => {
    if (stripeConnectStatus.connected) {
      try {
        const res = await stripeAPI.connectDashboardLink();
        const url = String(res?.data?.url || "");
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        }
      } catch (error) {
        console.warn("Stripe dashboard link failed, fallback to settings", error);
      }
    }
    guardedNavigate("/settings?tab=advanced&section=payments");
  }, [guardedNavigate, stripeConnectStatus.connected]);

  const getInitials = () => {
    const fn = authUser.first_name?.[0] || "";
    const ln = authUser.last_name?.[0] || "";
    const initials = `${fn}${ln}`.toUpperCase();
    if (initials) return initials;
    return (authUser.email?.[0] || "U").toUpperCase();
  };

  const displayName = (() => {
    const full = [authUser.first_name, authUser.last_name].filter(Boolean).join(" ");
    if (full) return full;
    if (authUser.email) return authUser.email.split("@")[0];
    return "Konto";
  })();

  const emailLine = authUser.email && authUser.email !== displayName ? authUser.email : null;
  const userAvatarUrl = String(matchingStaffAvatar || authUser.avatar_url || "").trim();
  const planName = String(subscription?.plan || '').toLowerCase() === 'growth' ? 'Business' : (subscription?.plan_name || subscription?.plan || "Plan");
  const paymentSetupAttention = !smsAccessRestricted && paymentSetupStatus?.orgId === authUser.org_id
    && onlinePaymentsAvailable(subscription) && needsPaymentSetup(paymentSetupStatus?.status);
  const planStatus = String(subscription?.status || "").toLowerCase();
  const planStatusLabel = (() => {
    // Technische Ladehinweise gehören nicht in die Kundennavigation. Der
    // Endpunkt wird im Hintergrund erneut abgefragt; bis dahin bleibt nur der
    // bekannte Planname sichtbar.
    if (subscriptionLoadFailed) return "";
    if (planStatus === "trial") return "Testphase aktiv";
    if (planStatus === "active") return "Dein Plan ist aktiv";
    if (planStatus === "past_due") return "Zahlung offen";
    if (planStatus === "cancelled" || planStatus === "canceled") return "Gekündigt";
    return "Dein Plan";
  })();
  const planDaysRemaining = getSubscriptionDaysRemaining(subscription);
  const planTimeLabel = planDaysRemaining === null
    ? ""
    : planDaysRemaining === 1
      ? "noch 1 Tag"
      : `noch ${planDaysRemaining} Tage`;
  const activeStudioLabel = activeLocation?.name || (role === "staff" ? "Alle meine Standorte" : "Alle Standorte");
  const activeStudioLogoUrl = activeLocation?.logo_url || "";
  const activeTheme = resolveDesignTheme(activeLocation || org || {});
  const sidebarTheme = getSidebarTheme(activeLocation || org || {});
  const planStatusColor = planStatus === "active" ? "#00ad41" : sidebarTheme.subtle;
  const appAccent = activeTheme.accent || activeTheme.primary || "#1D1D1F";
  const appAccentHover = alphaColor(appAccent, 0.92);
  const appAccentSoft = alphaColor(appAccent, 0.12);
  const appAccentBorder = alphaColor(appAccent, 0.26);
  const appAccentRing = alphaColor(appAccent, 0.22);
  const hasPausedSetupTour =
    Boolean(setupTourState?.currentStep) &&
    setupTourState?.active === false;
  const setupResumeTarget = hasPausedSetupTour
    ? buildSetupTourTarget(setupTourState.currentStep, { locations })
    : "/setup";
  const showSetupContinue =
    ["owner", "admin"].includes(role) &&
    location.pathname !== "/setup" &&
    !location.search.includes("setup=1") &&
    setupStatus &&
    !setupStatus.onboarding_dismissed &&
    !setupStatus.completed_at &&
    hasPausedSetupTour;
  const bookingPageUrl = activeLocation?.slug ? `/studio/${activeLocation.slug}` : "";
  const showBookingMenuItem = Boolean(bookingPageUrl);
  const showPaymentsMenuItem = ["owner", "admin"].includes(role) && hasFeature("online_payments");
  const isAdminVisit = authUser.is_admin_visit === true || authUser.auth_type === "admin_visit";
  const adminVisitOrgName = authUser.admin_visit_org_name || org?.name || "Organisation";
  const finishAdminVisit = useCallback(() => {
    const returnUrl = getVisitAuthReturnUrl();
    clearVisitAuthSession();
    if (returnUrl) {
      window.location.assign(returnUrl);
      return;
    }
    navigate("/admin");
  }, [navigate]);
  const getSidebarLinkClassName = ({ active = false, compact = false } = {}) =>
    `sidebar-link ${compact ? "text-sm py-1.5" : ""} ${active ? "active" : ""}`;
  const getSidebarLinkStyle = ({ active = false } = {}) => ({
    color: active ? sidebarTheme.text : sidebarTheme.muted,
    backgroundColor: active ? sidebarTheme.accentSoft : "transparent",
    borderColor: active ? sidebarTheme.accentBorder : "transparent",
    boxShadow: active ? `0 10px 24px ${sidebarTheme.accentGlow}` : "none",
  });
  const currentVersion = String(releaseNotes?.version || "").trim();

  return (
    <div
      className="min-h-screen bg-[#F5F5F7] overflow-x-hidden"
      style={{
        "--app-accent": appAccent,
        "--app-accent-hover": appAccentHover,
        "--app-accent-soft": appAccentSoft,
        "--app-accent-border": appAccentBorder,
        "--app-accent-ring": appAccentRing,
      }}
    >
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 xl:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`
        fixed top-0 left-0 z-50 h-full w-[280px] border-r
        transform transition-transform duration-300 ease-out
        xl:translate-x-0
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}
        style={{
          background: sidebarTheme.background,
          borderRightColor: sidebarTheme.border,
        }}
      >
        <div className="flex flex-col h-full relative">
          {/* Overlay – covers full sidebar (nav + plan section) while subscription loads */}
          <div
            className="absolute inset-0 z-20 pointer-events-none transition-opacity duration-300"
            style={{
              opacity: subscriptionLoaded ? 0 : 1,
              background: sidebarTheme.background,
            }}
            aria-hidden="true"
          />
          <div className="flex items-center justify-between h-16 px-6 border-b" style={{ borderBottomColor: sidebarTheme.border }}>
            <Link
              to={smsAccessRestricted ? "/calendar" : "/dashboard"}
              className="flex items-center"
              onClick={(event) => {
                if (blockUnsavedClientNavigation(smsAccessRestricted ? "/calendar" : "/dashboard")) {
                  event.preventDefault();
                  return;
                }
                setSidebarOpen(false);
              }}
            >
              <img
                src="/images/mib-logo-full-white.png"
                alt="MIB Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="xl:hidden p-2 rounded-lg text-white transition-colors"
              style={{ backgroundColor: "transparent" }}
              onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = sidebarTheme.panelStrong; }}
              onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {org && (
            <div className="px-4 py-4 border-b space-y-3" style={{ borderBottomColor: sidebarTheme.border }}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="flex w-full items-center gap-3 px-2 py-2 rounded-xl text-left transition-colors" style={{ backgroundColor: sidebarTheme.panel }}>
                    {activeStudioLogoUrl ? (
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-white border flex items-center justify-center" style={{ borderColor: sidebarTheme.border }}>
                        <img src={activeStudioLogoUrl} alt={activeStudioLabel} className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border"
                        style={{
                          backgroundColor: sidebarTheme.panelStrong,
                          borderColor: sidebarTheme.border,
                          color: sidebarTheme.muted,
                        }}
                      >
                        <Building2 className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{org?.name || "Standort"}</p>
                      <p className="text-xs mt-1 truncate" style={{ color: sidebarTheme.muted }}>Standort: {activeStudioLabel}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0" style={{ color: sidebarTheme.muted }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 rounded-xl">
                  {canSelectAll && (
                    <DropdownMenuItem onClick={() => setActiveLocationId(null)}>
                      {role === "staff" ? "Alle meine Standorte" : "Alle Standorte"}
                    </DropdownMenuItem>
                  )}
                  {locations.map((l) => (
                    <DropdownMenuItem key={l.id} onClick={() => setActiveLocationId(l.id)}>
                      <span className="truncate">{l.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {visibleNavigation.map((item) => {
              const Icon = item.icon;

              const navigateTo = (href) => (event) => {
                if (blockUnsavedClientNavigation(href)) {
                  event.preventDefault();
                  return;
                }
                setSidebarOpen(false);
              };
              const badge =
                item.href === "/messenger" && messengerUnreadBadge > 0 ? (
                  <span className="ml-2 inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-[#FF3B30] px-1.5 text-[11px] font-semibold text-white" aria-label={`${messengerUnreadBadge} ungelesene Messenger-Nachrichten`}>
                    {messengerUnreadBadge > 99 ? "99+" : messengerUnreadBadge}
                  </span>
                ) : item.href === "/anfragen" && newInquiryBadge > 0 ? (
                  <span className="ml-2 inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-[#FF3B30] px-1.5 text-[11px] font-semibold text-white" aria-label={`${newInquiryBadge} neue Anfragen`}>
                    {newInquiryBadge > 99 ? "99+" : newInquiryBadge}
                  </span>
                ) : item.href === "/shop" && shopOrdersBadge > 0 ? (
                  <span className="ml-2 inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold">{shopOrdersBadge > 99 ? "99+" : shopOrdersBadge}</span>
                ) : item.href === "/settings" ? <PaymentSetupIndicator show={paymentSetupAttention} /> : null;

              /* Group with children */
              if (item.children) {
                const groupChildren = item.children;
                const isChildOpen = groupChildren.some((c) => location.pathname === c.href);
                /* Gruppen ohne eigenes Ziel führen zum ersten sichtbaren Kind – Marketing
                   soll wie Kunden mit einem Klick etwas öffnen, nicht nur aufklappen.
                   Bewusst aus den bereits gefilterten Kindern, damit kein gesperrter
                   Eintrag angesteuert wird. */
                const groupHref = item.href || groupChildren[0]?.href || "";
                const isOnGroupPath = item.href ? location.pathname === item.href : isChildOpen;
                /* Ein aktives Kind klappt die Gruppe immer auf – sonst verschwände die
                   Markierung, wo man gerade ist. Sonst gilt die gemerkte Entscheidung. */
                const isExpanded = manualGroups[item.name] ?? (isOnGroupPath || isChildOpen);
                const isActive = item.href ? location.pathname === item.href : false;
                const highlight = isActive || (!item.href && isExpanded);
                return (
                  <div key={item.name}>
                    <div
                      className={`sidebar-link w-full justify-between ${highlight ? "active" : ""}`}
                      style={getSidebarLinkStyle({ active: highlight })}
                    >
                      {/* Der Name navigiert; das Chevron daneben klappt nur auf. */}
                      {groupHref ? (
                        <Link
                          to={groupHref}
                          onClick={navigateTo(groupHref)}
                          data-tour={NAV_TOUR_IDS[groupHref] || undefined}
                          className="flex flex-1 items-center gap-3 min-w-0"
                        >
                          <Icon className="w-5 h-5 shrink-0" style={{ color: highlight ? sidebarTheme.accent : undefined }} />
                          <span className="truncate">{item.name}</span>
                          {badge}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleGroup(item.name, isExpanded)}
                          className="flex flex-1 items-center gap-3 min-w-0"
                        >
                          <Icon className="w-5 h-5 shrink-0" style={{ color: highlight ? sidebarTheme.accent : undefined }} />
                          <span className="truncate">{item.name}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.name, isExpanded)}
                        className="ml-1 shrink-0 p-1 -mr-1"
                        aria-expanded={isExpanded}
                        aria-label={`${item.name} ${isExpanded ? "zuklappen" : "aufklappen"}`}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-0.5">
                        {groupChildren.map((child) => {
                          const isChildActive = location.pathname === child.href;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={navigateTo(child.href)}
                              className={getSidebarLinkClassName({ active: isChildActive, compact: true })}
                              style={getSidebarLinkStyle({ active: isChildActive })}
                            >
                              <span>{child.name}</span>
                              {isChildActive && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              /* Regular nav item */
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
              const itemHref = item.href === "/settings" && paymentSetupAttention ? PAYMENT_SETUP_URL : item.href;
              return (
                <Link
                  key={item.name}
                  to={itemHref}
                  onClick={navigateTo(itemHref)}
                  data-tour={NAV_TOUR_IDS[item.href] || undefined}
                  className={getSidebarLinkClassName({ active: isActive })}
                  style={getSidebarLinkStyle({ active: isActive })}
                >
                  <Icon className="w-5 h-5" style={{ color: isActive ? sidebarTheme.accent : undefined }} />
                  <span>{item.name}</span>
                  {badge}
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Plan Info Card at the bottom left */}
          {["owner", "admin"].includes(role) && (
            <div className="px-4 pb-3 mt-auto">
              <Link
                to="/upgrade"
                className="group relative flex items-center justify-between overflow-hidden rounded-2xl border p-4 backdrop-blur-md transition-all"
                style={{
                  borderColor: sidebarTheme.border,
                  backgroundColor: sidebarTheme.panel,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
                    style={{ backgroundColor: sidebarTheme.panelStrong }}
                  >
                    <Sparkles className="h-5 w-5" style={{ color: sidebarTheme.accent }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{planName}</div>
                    {planStatusLabel && <div className="text-xs font-semibold" style={{ color: planStatusColor }}>{planStatusLabel}</div>}
                    {planTimeLabel && (
                      <div className="mt-1 text-[11px] font-medium" style={{ color: sidebarTheme.accent }}>{planTimeLabel}</div>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" style={{ color: sidebarTheme.subtle }} />
              </Link>
            </div>
          )}

          <div className="px-4 pb-4 text-center">
            <button
              type="button"
              onClick={openVersionHistory}
              className="mx-auto text-[11px] transition-colors hover:text-white"
              style={{ color: sidebarTheme.subtle }}
            >
              {currentVersion ? `Version ${currentVersion}` : "Version"}
            </button>
          </div>

        </div>
      </aside>

      <ReleaseNotesDialog
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        notes={releaseNotes || FALLBACK_RELEASE_NOTES}
        loading={releaseNotesLoading}
        mode="history"
      />

      <div className="xl:pl-[280px]">
        {leadAlert ? (
          <div className="pointer-events-none fixed right-4 top-4 z-[72] w-[min(380px,calc(100vw-2rem))]">
            <button
              type="button"
              className="pointer-events-auto w-full rounded-2xl border border-[#7DD3FC] bg-white/95 p-4 text-left shadow-[0_18px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl transition hover:border-[#38BDF8]"
              onClick={() => {
                const targetUrl = leadAlert.targetUrl || "/dashboard";
                setLeadAlert(null);
                if (leadAlertTimeoutRef.current) window.clearTimeout(leadAlertTimeoutRef.current);
                guardedNavigate(targetUrl);
              }}
              data-testid="new-lead-alert"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-[#E0F2FE] text-[#0284C7]">
                  <UserPlus className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#0284C7]">Neue Anfrage</p>
                  <p className="mt-1 text-sm font-semibold text-[#1D1D1F]">{leadAlert.title}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-[#4B5563]">{leadAlert.body}</p>
                </div>
                <span className="rounded-full bg-[#F5F5F7] px-2 py-1 text-[11px] font-medium text-[#1D1D1F]">
                  Öffnen
                </span>
              </div>
            </button>
          </div>
        ) : null}
        {messengerAlert ? (
          <div className={`pointer-events-none fixed right-4 z-[70] w-[min(360px,calc(100vw-2rem))] ${leadAlert ? "top-44" : "top-4"}`}>
            <button
              type="button"
              className="pointer-events-auto w-full rounded-2xl border border-[#E5E5EA] bg-white/95 p-4 text-left shadow-[0_18px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl transition hover:border-[#FF3B30]/40"
              onClick={() => {
                const threadId = String(messengerAlert.threadId || "").trim();
                setMessengerAlert(null);
                if (messengerAlertTimeoutRef.current) {
                  window.clearTimeout(messengerAlertTimeoutRef.current);
                }
                guardedNavigate(threadId ? `/messenger?thread_id=${encodeURIComponent(threadId)}` : "/messenger");
              }}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-[#FF3B30]/10 text-[#FF3B30]">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#FF3B30]">Neue Nachricht</p>
                  <p className="mt-1 text-sm font-semibold text-[#1D1D1F]">{messengerAlert.title}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-[#4B5563]">{messengerAlert.body}</p>
                </div>
                <span className="rounded-full bg-[#F5F5F7] px-2 py-1 text-[11px] font-medium text-[#1D1D1F]">
                  Öffnen
                </span>
              </div>
            </button>
          </div>
        ) : null}
        <header
          className={`sticky top-0 z-30 bg-white/80 backdrop-blur-xl transition-[height,opacity,transform,border-color] duration-300 ease-out ${
            !isAutoHideHeaderPage || messengerHeaderVisible || searchActive
              ? "h-16 translate-y-0 border-b border-[#E5E5EA] opacity-100"
              : "pointer-events-none h-0 -translate-y-full overflow-hidden border-transparent opacity-0"
          }`}
        >
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0 mr-2 lg:mr-4">
              <button onClick={() => setSidebarOpen(true)} className="xl:hidden p-2 hover:bg-[#F5F5F7] rounded-lg">
                <Menu className="w-5 h-5" />
              </button>
              <GlobalSearch key={`${authUser.id}:${authUser.org_id}:${activeLocationId || 'all'}:${location.pathname}`}
                navigation={visibleNavigation} personLabel={vocab.person.pl}
                canReadClients={!smsAccessRestricted && hasPermission('clients:read')}
                canReadCalendar={hasPermission('calendar:read')}
                onNavigate={guardedNavigate} onActiveChange={setSearchActive} />
              {locations.length > 0 && (
                <div className="flex-1 min-w-0 xl:hidden">
                  <Select
                    value={activeLocationId || "all"}
                    onValueChange={(value) => setActiveLocationId(value === "all" ? null : value)}
                  >
                    <SelectTrigger className="w-full min-w-0 min-h-[44px] [&>span]:line-clamp-1 [&>span]:truncate bg-white border border-[#E5E5EA] rounded-xl px-3 py-2 text-sm font-medium text-[#1D1D1F]">
                      <SelectValue placeholder="Standort wählen" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {canSelectAll && (
                        <SelectItem value="all">{role === "staff" ? "Alle meine Standorte" : "Alle Standorte"}</SelectItem>
                      )}
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>


            <div className="flex items-center gap-2 lg:gap-3">
              {showSetupContinue && (
                <button
                  type="button"
                  onClick={() => guardedNavigate(setupResumeTarget)}
                  className="hidden xl:flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#1D1D1F] bg-white border border-[#E5E5EA] hover:border-black rounded-xl transition-colors"
                >
                  <span>Setup abschließen</span>
                </button>
              )}
              {locations.length > 0 && (
                <div className="hidden xl:block w-[190px] 2xl:w-[240px]">
                  <Select
                    value={activeLocationId || "all"}
                    onValueChange={(value) => setActiveLocationId(value === "all" ? null : value)}
                  >
                    <SelectTrigger
                      className="w-full bg-white border border-[#E5E5EA] rounded-xl px-3 py-2 text-sm font-medium text-[#1D1D1F] transition-all"
                      style={{ "--tw-ring-color": appAccentRing, borderColor: "#E5E5EA" }}
                    >
                      <SelectValue placeholder="Standort wählen" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {canSelectAll && (
                        <SelectItem value="all">{role === "staff" ? "Alle meine Standorte" : "Alle Standorte"}</SelectItem>
                      )}
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DropdownMenu open={notificationsOpen} onOpenChange={(open) => {
                setNotificationsOpen(open);
                if (!open) setNotificationSessionNewIds(new Set());
              }}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="order-2 relative flex h-11 w-11 items-center justify-center rounded-xl border border-[#E5E5EA] bg-white text-[#1D1D1F] transition-colors"
                    style={{ borderColor: "#E5E5EA" }}
                    aria-label="Benachrichtigungen"
                  >
                    <Bell className="h-5 w-5" style={{ color: notificationsOpen ? appAccent : undefined }} />
                    {notificationUnread > 0 && (
                      <span
                        className="absolute -right-1 -top-1 inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold"
                        aria-label={`${notificationUnread} ungelesene Benachrichtigungen`}
                      >
                        {formatNotificationBadgeCount(notificationUnread)}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={12} className="w-[360px] rounded-2xl border border-[#E5E5EA] bg-white p-0 shadow-[0_18px_60px_rgba(15,23,42,0.12)]">
                  <div className="border-b border-[#E5E5EA] px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#1D1D1F]">Benachrichtigungen</p>
                        <p className="text-xs text-[#86868B]">Neue Anfragen, Buchungen, Stornos und Zahlungen</p>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          markAllNotificationsRead();
                        }}
                        disabled={notificationSessionNewIds.size === 0 && notificationUnread === 0}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-medium text-[#0066CC] transition-colors hover:bg-[#E7F2FF] disabled:cursor-default disabled:text-[#A1A1AA] disabled:hover:bg-transparent"
                        data-testid="mark-all-notifications-read"
                      >
                        <CheckCheck className="h-3.5 w-3.5" />
                        {notificationSessionNewIds.size > 0 || notificationUnread > 0
                          ? "Alle als gelesen"
                          : "Alles gelesen"}
                      </button>
                    </div>
                  </div>
                  <div ref={notificationScrollRef} className="max-h-[420px] overflow-y-auto">
                    {notificationsLoading ? (
                      <ListLoading label="Benachrichtigungen werden geladen …" />
                    ) : notificationItems.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-[#86868B]">Keine neuen Benachrichtigungen vorhanden.</div>
                    ) : (
                      notificationItems.map((item) => {
                        const Icon = getNotificationIcon(item.event_type);
                        const isNew = notificationSessionNewIds.has(item.id);
                        return (
                          <button
                            key={item.id}
                            data-notification-id={item.id}
                            type="button"
                            onClick={() => openNotificationTarget(item)}
                            className={`flex w-full items-start gap-3 border-b px-4 py-4 text-left transition-colors ${
                              isNew
                                ? "border-l-2 border-b-[#D9EAFE] border-l-[#0071E3] bg-[#F0F7FF] hover:bg-[#E7F2FF]"
                                : "border-b-[#F1F2F4] bg-white opacity-70 hover:bg-[#FAFAFB] hover:opacity-100"
                            }`}
                            data-notification-state={isNew ? "new" : "read"}
                          >
                            <div className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F5F7] ${getNotificationIconClass(item.event_type)}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <p className={`text-xs font-semibold uppercase tracking-[0.04em] ${isNew ? "text-[#0066CC]" : "text-[#86868B]"}`}>
                                  {notificationText(item.title)}{isNew ? <span className="ml-2 rounded-full bg-[#0071E3] px-1.5 py-0.5 text-[9px] text-white">Neu</span> : null}
                                </p>
                                <span className="shrink-0 text-[11px] text-[#A1A1AA]">{formatNotificationRelativeTime(item.happened_at)}</span>
                              </div>
                              <p className="mt-1 text-sm font-semibold text-[#1D1D1F]">
                                {item.service_name || (item.event_type === "lead_created" ? "Behandlung noch nicht festgelegt" : "Leistung")}
                              </p>
                              <p className="mt-1 text-sm text-[#4B5563]">
                                {item.client_name || `Unbekannter ${vocab.person.sg}`}
                                {item.amount ? ` · ${Number(item.amount).toFixed(2)} ${item.currency || "EUR"}` : ""}
                              </p>
                              <p className="mt-1 text-xs text-[#86868B]">
                                {formatNotificationDate(item.start_time || item.happened_at)}
                                {item.location_name ? ` · ${item.location_name}` : ""}
                              </p>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              {activeLocation?.slug && (
                <a
                  href={`/studio/${activeLocation?.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tour="setup-booking-link"
                  onClick={() => {
                    if (!location.search.includes("setup=1") || !location.search.includes("setupStep=booking")) return;
                    setupAPI.updateStatus({ booking_tested: true })
                      .then(() => setSetupStatus((prev) => ({ ...(prev || {}), booking_tested: true })))
                      .catch(() => {})
                      .finally(() => dispatchSetupTourStatusChanged());
                  }}
                  className="order-1 hidden md:flex items-center gap-2 px-3 lg:px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors"
                  style={{ backgroundColor: appAccent }}
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden 2xl:inline">Standort-Seite öffnen</span>
                  <span className="hidden md:inline 2xl:hidden">Buchungsseite</span>
                </a>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="order-3 flex items-center gap-2 lg:gap-3 px-2 py-2 min-h-[44px] rounded-xl hover:bg-[#F5F5F7] active:bg-[#E5E5EA] transition-colors cursor-pointer touch-target">
                    <Avatar className="w-9 h-9 flex-shrink-0">
                      {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt={displayName} className="object-cover" />}
                      <AvatarFallback className="bg-[#1D1D1F] text-white font-medium">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left min-w-0 max-w-[150px] 2xl:max-w-[220px] hidden sm:block">
                      <p className="text-sm font-medium text-[#1D1D1F] truncate">{displayName}</p>
                      {emailLine && <p className="text-xs text-[#86868B] truncate">{emailLine}</p>}
                    </div>
                    <ChevronDown className="hidden sm:block w-4 h-4 text-[#86868B]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {showBookingMenuItem && (
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        window.open(bookingPageUrl, "_blank", "noopener,noreferrer");
                      }}
                      className="cursor-pointer"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Buchungsseite öffnen
                    </DropdownMenuItem>
                  )}
                  {showPaymentsMenuItem && (
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        void handleOpenPayments();
                      }}
                      className="cursor-pointer"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Zahlungen empfangen
                    </DropdownMenuItem>
                  )}
                  {(showBookingMenuItem || showPaymentsMenuItem) && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={() => guardedNavigate("/upgrade")} className="cursor-pointer">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Plan &amp; Upgrade
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {hasPermission("settings:manage") && (
                    <>
                      <DropdownMenuItem onClick={() => guardedNavigate("/settings")} className="cursor-pointer">
                        <Settings className="w-4 h-4 mr-2" />
                        Einstellungen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                    className="text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {isAdminVisit && (
          <div className="sticky top-16 z-20 border-b border-[#F59E0B]/30 bg-[#FFFBEB] px-4 py-3 lg:px-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#92400E]">
                  Superadmin-Besuch: {adminVisitOrgName}
                </p>
                <p className="text-xs text-[#B45309]">
                  Du arbeitest {role === 'owner' || role === 'admin' ? 'mit Inhaberrechten' : role === 'reception' ? 'mit Empfangsrechten' : 'mit Mitarbeiterrechten'} in dieser Organisation.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={finishAdminVisit}
                  className="rounded-xl border border-[#F59E0B]/40 bg-white px-3 py-2 text-sm font-medium text-[#92400E] transition-colors hover:bg-[#FEF3C7]"
                >
                  Zurück zum Admin-Dashboard
                </button>
                <button
                  type="button"
                  onClick={finishAdminVisit}
                  className="rounded-xl bg-[#92400E] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#78350F]"
                >
                  Besuch beenden
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="p-4 sm:p-5 lg:p-6 w-full min-w-0 overflow-x-hidden">
          {billingAccess.payment_review_required && ['owner', 'admin'].includes(role) && (
            <div role="status" className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
              <p className="font-semibold">Erstattung oder Zahlungsstreit zu deinem Abo</p>
              <p className="text-sm">Die Zahlung muss geklärt werden. Dein bezahlter Nutzungszeitraum wird dadurch nicht automatisch geändert. Bitte kontaktiere service@makeit-booking.de.</p>
            </div>
          )}
          {(softwareAccessRestricted || billingAccess.reason === 'software_invoice_unpaid') && (
            <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
              <div><p className="font-semibold">{softwareAccessRestricted ? 'Dein Jahresabo muss geprüft oder verlängert werden' : 'Eine Rechnung zu deinem Abo ist offen'}</p>
                <p className="text-sm">{softwareAccessRestricted ? 'Deine Daten bleiben erhalten. Du kannst sie weiterhin ansehen und exportieren; Änderungen sind vorübergehend gesperrt.' : 'Dein Zugang bleibt bis zum Ende des bezahlten Nutzungszeitraums verfügbar.'} Bei Fragen hilft service@makeit-booking.de.</p></div>
              {['owner', 'admin'].includes(role) && location.pathname !== '/upgrade' && <Link to="/upgrade" className="rounded-xl bg-amber-900 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800">Abrechnung prüfen</Link>}
            </div>
          )}
          {smsPaymentWarning && (
            <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
              <div>
                <p className="font-semibold">{smsAccessRestricted ? "Zugriff wegen offener SMS-Rechnung eingeschränkt" : billingAccess.mode === "processing" ? "Zahlung wird verarbeitet" : "SMS-Rechnung noch offen"}</p>
                <p className="text-sm">
                  {smsAccessRestricted
                    ? "Der Kalender bleibt lesbar. Weitere Funktionen sind bis zur Zahlung gesperrt."
                    : billingAccess.mode === "processing"
                      ? "Stripe verarbeitet die Zahlung noch. Dein Zugang bleibt währenddessen vollständig geöffnet."
                    : `Bitte bezahle die SMS-Rechnung bis ${new Date(billingAccess.grace_until).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}. SMS-Versand ist bereits pausiert; danach bleibt nur der Kalender lesbar.`}
                  {!["owner", "admin"].includes(role) && " Bitte informiere den Inhaber der Organisation."}
                </p>
              </div>
              {["owner", "admin"].includes(role) && location.pathname !== "/upgrade" && (
                <Link to="/upgrade" className="rounded-xl bg-amber-900 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800">Rechnung bezahlen</Link>
              )}
            </div>
          )}
          <DashboardPageContent>{children ?? <Outlet />}</DashboardPageContent>
        </main>
      </div>
      {!smsAccessRestricted && !softwareAccessRestricted && <SetupTourOverlay />}
    </div>
  );
}
